import type { Theme } from '@mui/material/styles';
import type { ReactNode, MouseEvent } from 'react';

import { toast } from 'sonner';
import { varAlpha } from 'minimal-shared/utils';

import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';

import { Iconify } from '../iconify';

// ----------------------------------------------------------------------

export type ToastTone = 'neutral' | 'success' | 'info' | 'warning' | 'error' | 'loading';

export type ToastButton = {
  label: ReactNode;
  onClick: (event: MouseEvent<HTMLButtonElement>) => void;
};

type ToastCardProps = {
  id: string | number;
  tone: ToastTone;
  title: ReactNode;
  description?: ReactNode;
  /** Replaces the tone's icon; `null` shows none. */
  icon?: ReactNode;
  action?: ToastButton;
  cancel?: ToastButton;
  closeButton?: boolean;
};

const TONE_ICONS: Record<ToastTone, ReactNode> = {
  neutral: null,
  success: <Iconify icon="solar:check-circle-bold" width={20} />,
  info: <Iconify icon="solar:info-circle-bold" width={20} />,
  warning: <Iconify icon="solar:danger-triangle-bold" width={20} />,
  error: <Iconify icon="solar:danger-bold" width={20} />,
  loading: <CircularProgress size={18} thickness={5} color="inherit" />,
};

const TONE_COLORS: Record<ToastTone, string> = {
  neutral: 'primary.main',
  success: 'success.main',
  info: 'info.main',
  warning: 'warning.main',
  error: 'error.main',
  loading: 'primary.main',
};

/** One toast, every tone: an icon, what happened, what to do next, and up to two buttons. */
export function ToastCard({
  id,
  tone,
  title,
  description,
  icon,
  action,
  cancel,
  closeButton = true,
}: ToastCardProps) {
  const toneIcon = icon === undefined ? TONE_ICONS[tone] : icon;
  const dismiss = () => toast.dismiss(id);

  // As with Sonner's own buttons, a press dismisses the toast unless its handler prevents that.
  const press = (button: ToastButton) => (event: MouseEvent<HTMLButtonElement>) => {
    button.onClick(event);
    if (!event.defaultPrevented) dismiss();
  };

  return (
    <Paper sx={CardSx}>
      <Stack direction="row" spacing={1.5} alignItems="flex-start">
        {toneIcon && <Box sx={[IconSx, { color: TONE_COLORS[tone] }]}>{toneIcon}</Box>}

        <Box sx={TextSx}>
          <Typography variant="subtitle2">{title}</Typography>
          {description && (
            <Typography variant="body2" color="text.secondary">
              {description}
            </Typography>
          )}
        </Box>

        {closeButton && (
          <IconButton size="small" aria-label="Dismiss" onClick={dismiss} sx={CloseSx}>
            <Iconify icon="mingcute:close-line" width={16} />
          </IconButton>
        )}
      </Stack>

      {(action || cancel) && (
        <Stack direction="row" justifyContent="flex-end" spacing={1} sx={{ mt: 1.5 }}>
          {cancel && (
            <Button size="small" color="inherit" onClick={press(cancel)}>
              {cancel.label}
            </Button>
          )}
          {action && (
            <Button size="small" variant="contained" color="primary" onClick={press(action)}>
              {action.label}
            </Button>
          )}
        </Stack>
      )}
    </Paper>
  );
}

// ----------------------------------------------------------------------

const CardSx = {
  p: 2,
  borderRadius: 1.5,
  border: (theme: Theme) => `solid 1px ${varAlpha(theme.vars.palette.grey['500Channel'], 0.16)}`,
  boxShadow: (theme: Theme) => theme.vars.customShadows.card,
} as const;

// As tall as the title's first line, so the icon centres on it.
const IconSx = {
  height: 22,
  flexShrink: 0,
  display: 'flex',
  alignItems: 'center',
} as const;

const TextSx = {
  flex: 1,
  minWidth: 0,
  overflowWrap: 'anywhere',
} as const;

// Pulled into the corner so it sits level with the title without widening the card.
const CloseSx = {
  m: -0.75,
  color: 'text.disabled',
} as const;
